import React, { useState, useEffect } from 'react';
import { useNavigate, Navigate } from 'react-router-dom';
import { 
  Building2, Users, TrendingUp, FileText, Settings, LogOut, Menu, X,
  Home, Inbox, Calendar, Bell, Search, ChevronRight, Shield, Star,
  Eye, Heart, Clock, CheckCircle2, AlertCircle, Plus,
  Handshake
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { Progress } from '@/components/ui/progress';
import { Separator } from '@/components/ui/separator';
import { useAuth } from '@/context/AuthContext';
import api from '@/services/api';
import BuyerTransactionProfile from '@/components/BuyerTransactionProfile';
import AgentPerformanceScore from '@/components/AgentPerformanceScore';
import UserObligationsPanel from '@/components/UserObligationsPanel';
import AEDIcon from '@/components/icons/AEDIcon';

const DashboardLayout = ({ children, activeTab, setActiveTab }) => {
  const navigate = useNavigate();
  const { user, logout } = useAuth();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  const navItems = [
    { id: 'dashboard', label: 'Dashboard', icon: Home },
    { id: 'properties', label: user?.role === 'buyer' ? 'Browse Properties' : 'My Listings', icon: Building2 },
    { id: 'viewings', label: 'Viewings', icon: Eye },
    { id: 'deals', label: 'My Deals', icon: Handshake },
    { id: 'interests', label: user?.role === 'buyer' ? 'My Shortlist' : 'Interests', icon: Heart },
    { id: 'offers', label: 'Offers', icon: AEDIcon },
    { id: 'calendar', label: 'Calendar', icon: Calendar },
  ];

  if (user?.role === 'admin') {
    navItems.push({ id: 'admin', label: 'Admin Panel', icon: Settings });
  }

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-background flex">
      <div className="noise-overlay" />
      
      {/* Sidebar */}
      <aside className={`fixed inset-y-0 left-0 z-50 w-64 bg-card border-r border-border transform transition-transform duration-300 lg:translate-x-0 ${
        sidebarOpen ? 'translate-x-0' : '-translate-x-full'
      }`}>
        <div className="flex flex-col h-full">
          {/* Logo */}
          <div className="p-6 border-b border-border">
            <div className="flex items-center gap-2">
              <div className="w-10 h-10 rounded bg-primary flex items-center justify-center">
                <span className="font-display text-xl text-primary-foreground font-bold">R</span>
              </div>
              <span className="font-display text-2xl font-medium tracking-tight">Rashii</span>
            </div>
          </div>
          
          {/* Nav */}
          <nav className="flex-1 p-4 space-y-1">
            {navItems.map((item) => (
              <button
                key={item.id}
                onClick={() => {
                  if (item.id === 'deals') {
                    navigate('/deals');
                  } else if (item.id === 'viewings') {
                    navigate('/viewings');
                  } else if (item.id === 'admin') {
                    navigate('/admin');
                  } else {
                    setActiveTab(item.id);
                  }
                  setSidebarOpen(false);
                }}
                className={`w-full flex items-center gap-3 px-4 py-3 rounded text-sm transition-colors duration-300 ${
                  activeTab === item.id 
                    ? 'bg-primary/10 text-primary border border-primary/30' 
                    : 'text-muted-foreground hover:text-foreground hover:bg-secondary/50'
                }`}
                data-testid={`nav-${item.id}`}
              >
                <item.icon className="w-5 h-5" />
                {item.label}
              </button>
            ))}
          </nav>
          
          {/* User */}
          <div className="p-4 border-t border-border">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                <span className="text-sm font-medium">{user?.username?.[0]?.toUpperCase()}</span>
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-sm font-medium truncate">{user?.username}</div>
                <div className="text-xs text-muted-foreground capitalize">{user?.role}</div>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" size="sm" className="flex-1 text-xs" onClick={() => setActiveTab('settings')}>
                <Settings className="w-4 h-4 mr-1" /> Settings
              </Button>
              <Button variant="outline" size="sm" className="text-xs text-destructive hover:bg-destructive/10" onClick={handleLogout} data-testid="logout-btn">
                <LogOut className="w-4 h-4" />
              </Button>
            </div>
          </div>
        </div>
      </aside>
      
      {/* Main Content */}
      <div className="flex-1 lg:ml-64">
        {/* Top Bar */}
        <header className="sticky top-0 z-40 backdrop-blur-xl border-b border-border h-16 flex items-center px-6">
          <button
            className="lg:hidden mr-4"
            onClick={() => setSidebarOpen(!sidebarOpen)}
          >
            {sidebarOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
          </button>
          
          {/* Home Button */}
          <Button
            variant="ghost"
            size="sm"
            onClick={() => navigate('/')}
            className="text-muted-foreground hover:text-foreground mr-4"
            data-testid="home-btn"
          >
            <Home className="w-4 h-4 mr-1" />
            Home
          </Button>
          
          <div className="flex-1" />
          
          <div className="flex items-center gap-4">
            <Button 
              variant="ghost" 
              size="icon" 
              className="relative"
              onClick={() => alert('Notifications coming soon')}
              data-testid="notifications-btn"
            >
              <Bell className="w-5 h-5" />
              <span className="absolute top-1 right-1 w-2 h-2 bg-primary rounded-full" />
            </Button>
            
            {/* Verification Status - Clickable */}
            <div className="hidden md:flex items-center gap-2">
              {user?.kyc_status === 'verified' ? (
                <Badge className="bg-green-600/20 text-green-400 border-green-600/30">
                  <CheckCircle2 className="w-3 h-3 mr-1" /> KYC Verified
                </Badge>
              ) : (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => {
                    if (user?.role === 'seller') {
                      navigate('/seller/onboarding');
                    } else {
                      navigate('/onboarding');
                    }
                  }}
                  className="p-0 h-auto"
                  data-testid="kyc-status-btn"
                >
                  <Badge className="bg-gold-600/20 text-gold-400 border-gold-600/30 cursor-pointer hover:bg-gold-600/30">
                    <AlertCircle className="w-3 h-3 mr-1" /> KYC Pending - Click to Complete
                  </Badge>
                </Button>
              )}
            </div>
          </div>
        </header>
        
        {/* Page Content */}
        <main className="p-6">
          {children}
        </main>
      </div>
      
      {/* Overlay */}
      {sidebarOpen && (
        <div 
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

const BuyerDashboard = () => {
  const { user } = useAuth();
  const [shortlist, setShortlist] = useState([]);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [shortlistRes, offersRes] = await Promise.all([
          api.get('/interests/my-shortlist'),
          api.get('/offers/buyer/my-offers')
        ]);
        setShortlist(shortlistRes.data);
        setOffers(offersRes.data);
      } catch (error) {
        console.error('Failed to fetch buyer data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  return (
    <div className="space-y-8" data-testid="buyer-dashboard">
      <div>
        <h1 className="font-display text-3xl tracking-tight">Welcome back</h1>
        <p className="text-muted-foreground">Manage your property search and offers</p>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="glass-card border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Shortlisted</p>
                <p className="text-3xl font-display text-primary mt-1">{shortlist.length}/3</p>
              </div>
              <Heart className="w-8 h-8 text-primary/50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="glass-card border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Active Offers</p>
                <p className="text-3xl font-display text-primary mt-1">{offers.filter(o => o.status === 'pending').length}</p>
              </div>
              <AEDIcon className="w-8 h-8 text-primary/50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="glass-card border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground">Viewings</p>
                <p className="text-3xl font-display text-primary mt-1">{shortlist.filter(s => s.stage === 'viewed').length}</p>
              </div>
              <Eye className="w-8 h-8 text-primary/50" />
            </div>
          </CardContent>
        </Card>
        
        <Card className="glass-card border-white/10">
          <CardContent className="p-6">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-xs uppercase tracking-widest text-muted-foreground">In Negotiation</p>
                <p className="text-3xl font-display text-primary mt-1">{offers.filter(o => o.status === 'countered').length}</p>
              </div>
              <TrendingUp className="w-8 h-8 text-primary/50" />
            </div>
          </CardContent>
        </Card>
      </div>
      
      {/* Transaction Profile / Performance Score - Soft Visibility */}
      {user?.role === 'agent' ? (
        <AgentPerformanceScore 
          token={localStorage.getItem('rashii_access_token')} 
          agentId={user?.id} 
        />
      ) : (
        <BuyerTransactionProfile 
          token={localStorage.getItem('rashii_access_token')} 
          buyerId={user?.id} 
        />
      )}
      
      {/* User Obligations - Phase 2 Soft Visibility */}
      <UserObligationsPanel role={user?.role || 'buyer'} maxItems={3} />
      
      {/* Shortlist Progress */}
      <Card className="glass-card border-white/10">
        <CardHeader>
          <CardTitle className="font-display text-xl">Your Property Journey</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="flex items-center justify-between text-sm">
              <span className="text-muted-foreground">Shortlist Progress</span>
              <span className="text-primary">{shortlist.length} of 3 slots used</span>
            </div>
            <Progress value={(shortlist.length / 3) * 100} className="h-2" />
            <p className="text-xs text-muted-foreground">
              You can shortlist up to 3 properties for viewing. Make your selections count!
            </p>
          </div>
        </CardContent>
      </Card>
      
      {/* Recent Offers */}
      <Card className="glass-card border-white/10">
        <CardHeader className="flex flex-row items-center justify-between">
          <CardTitle className="font-display text-xl">Recent Offers</CardTitle>
          <Button variant="outline" size="sm" className="text-xs">
            View All <ChevronRight className="w-4 h-4 ml-1" />
          </Button>
        </CardHeader>
        <CardContent>
          {offers.length > 0 ? (
            <div className="space-y-4">
              {offers.slice(0, 3).map((offer) => (
                <div key={offer.id} className="flex items-center justify-between p-4 rounded border border-border hover:border-primary/30 transition-colors">
                  <div>
                    <p className="font-medium">AED {offer.offer_amount.toLocaleString()}</p>
                    <p className="text-xs text-muted-foreground">Submitted {new Date(offer.created_at).toLocaleDateString()}</p>
                  </div>
                  <Badge className={{
                    'pending': 'bg-gold-600/20 text-gold-400',
                    'accepted': 'bg-green-600/20 text-green-400',
                    'rejected': 'bg-red-600/20 text-red-400',
                    'countered': 'bg-blue-600/20 text-blue-400'
                  }[offer.status]}>
                    {offer.status}
                  </Badge>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">No offers yet. Browse properties to get started.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const SellerDashboard = () => {
  const navigate = useNavigate();
  const [dashboard, setDashboard] = useState(null);
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [dashboardRes, offersRes] = await Promise.all([
          api.get('/seller/dashboard'),
          api.get('/offers/seller/received')
        ]);
        setDashboard(dashboardRes.data);
        setOffers(offersRes.data);
      } catch (error) {
        console.error('Failed to fetch seller data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  const handleOfferAction = async (offerId, action) => {
    try {
      await api.put(`/offers/${offerId}/${action}`);
      // Refresh offers
      const offersRes = await api.get('/offers/seller/received');
      setOffers(offersRes.data);
    } catch (error) {
      console.error('Failed to update offer:', error);
    }
  };

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-8" data-testid="seller-dashboard">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="font-display text-3xl tracking-tight">Seller Dashboard</h1>
          <p className="text-muted-foreground">Manage your listings and track buyer interest</p>
        </div>
        <Button className="bg-primary text-primary-foreground" onClick={() => navigate('/seller/property/create')} data-testid="add-property-btn">
          <Plus className="w-4 h-4 mr-2" /> Add Property
        </Button>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <Card className="glass-card border-white/10">
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Properties</p>
            <p className="text-3xl font-display text-primary mt-1">{dashboard?.summary?.total_properties || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="glass-card border-white/10">
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Interested</p>
            <p className="text-3xl font-display text-primary mt-1">{dashboard?.summary?.total_interested || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="glass-card border-white/10">
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Shortlisted</p>
            <p className="text-3xl font-display text-primary mt-1">{dashboard?.summary?.total_shortlisted || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="glass-card border-white/10">
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Viewings</p>
            <p className="text-3xl font-display text-primary mt-1">{dashboard?.summary?.total_viewings || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="glass-card border-white/10">
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Pending Offers</p>
            <p className="text-3xl font-display text-primary mt-1">{dashboard?.summary?.pending_offers || 0}</p>
          </CardContent>
        </Card>
      </div>
      
      {/* Property Interest Funnel */}
      <Card className="glass-card border-white/10">
        <CardHeader>
          <CardTitle className="font-display text-xl">Interest Funnel by Property</CardTitle>
        </CardHeader>
        <CardContent>
          {dashboard?.properties?.length > 0 ? (
            <div className="space-y-6">
              {dashboard.properties.map((prop) => (
                <div key={prop.property_id} className="p-4 rounded border border-border">
                  <div className="flex items-center justify-between mb-4">
                    <div>
                      <h4 className="font-medium">{prop.title}</h4>
                      <p className="text-sm text-primary">AED {prop.price.toLocaleString()}</p>
                    </div>
                  </div>
                  <div className="grid grid-cols-5 gap-2 text-center text-xs">
                    {['interested', 'video_watched', 'shortlisted', 'viewing_requested', 'viewed'].map((stage) => (
                      <div key={stage} className="p-2 rounded bg-secondary/50">
                        <p className="text-lg font-bold text-primary">{prop.stages?.[stage] || 0}</p>
                        <p className="text-muted-foreground capitalize">{stage.replace('_', ' ')}</p>
                      </div>
                    ))}
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">No properties listed yet.</p>
          )}
        </CardContent>
      </Card>
      
      {/* Pending Offers */}
      <Card className="glass-card border-white/10">
        <CardHeader>
          <CardTitle className="font-display text-xl">Pending Offers</CardTitle>
        </CardHeader>
        <CardContent>
          {offers.filter(o => o.status === 'pending').length > 0 ? (
            <div className="space-y-4">
              {offers.filter(o => o.status === 'pending').map((offer) => (
                <div key={offer.id} className="p-4 rounded border border-border">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-xl font-display text-primary">AED {offer.offer_amount.toLocaleString()}</p>
                      <p className="text-xs text-muted-foreground">From verified buyer • {new Date(offer.created_at).toLocaleDateString()}</p>
                    </div>
                    <div className="flex gap-2">
                      <Button 
                        size="sm" 
                        className="bg-green-600 hover:bg-green-700 text-white"
                        onClick={() => handleOfferAction(offer.id, 'accept')}
                      >
                        Accept
                      </Button>
                      <Button 
                        size="sm" 
                        variant="outline"
                        onClick={() => handleOfferAction(offer.id, 'reject')}
                      >
                        Reject
                      </Button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-center text-muted-foreground py-8">No pending offers at the moment.</p>
          )}
        </CardContent>
      </Card>
    </div>
  );
};

const AdminDashboard = () => {
  const [stats, setStats] = useState(null);
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const fetchData = async () => {
      try {
        const [statsRes, usersRes] = await Promise.all([
          api.get('/admin/stats'),
          api.get('/admin/users?limit=10')
        ]);
        setStats(statsRes.data);
        setUsers(usersRes.data);
      } catch (error) {
        console.error('Failed to fetch admin data:', error);
      } finally {
        setLoading(false);
      }
    };
    fetchData();
  }, []);

  if (loading) {
    return <div className="flex items-center justify-center h-64">Loading...</div>;
  }

  return (
    <div className="space-y-8" data-testid="admin-dashboard">
      <div>
        <h1 className="font-display text-3xl tracking-tight">Admin Dashboard</h1>
        <p className="text-muted-foreground">Platform overview and management</p>
      </div>
      
      {/* Stats */}
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Card className="glass-card border-white/10">
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Total Users</p>
            <p className="text-3xl font-display text-primary mt-1">{stats?.total_users || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="glass-card border-white/10">
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Properties</p>
            <p className="text-3xl font-display text-primary mt-1">{stats?.total_properties || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="glass-card border-white/10">
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Pending Verification</p>
            <p className="text-3xl font-display text-primary mt-1">{stats?.pending_verifications || 0}</p>
          </CardContent>
        </Card>
        
        <Card className="glass-card border-white/10">
          <CardContent className="p-6">
            <p className="text-xs uppercase tracking-widest text-muted-foreground">Total Offers</p>
            <p className="text-3xl font-display text-primary mt-1">{stats?.total_offers || 0}</p>
          </CardContent>
        </Card>
      </div>
      
      {/* Users by Role */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <Card className="glass-card border-white/10">
          <CardContent className="p-6 text-center">
            <p className="text-4xl font-display text-primary">{stats?.users_by_role?.buyers || 0}</p>
            <p className="text-sm text-muted-foreground mt-2">Buyers</p>
          </CardContent>
        </Card>
        
        <Card className="glass-card border-white/10">
          <CardContent className="p-6 text-center">
            <p className="text-4xl font-display text-primary">{stats?.users_by_role?.sellers || 0}</p>
            <p className="text-sm text-muted-foreground mt-2">Sellers</p>
          </CardContent>
        </Card>
        
        <Card className="glass-card border-white/10">
          <CardContent className="p-6 text-center">
            <p className="text-4xl font-display text-primary">{stats?.users_by_role?.agents || 0}</p>
            <p className="text-sm text-muted-foreground mt-2">Agents</p>
          </CardContent>
        </Card>
      </div>
      
      {/* Recent Users */}
      <Card className="glass-card border-white/10">
        <CardHeader>
          <CardTitle className="font-display text-xl">Recent Users</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-2">
            {users.map((user) => (
              <div key={user.id} className="flex items-center justify-between p-3 rounded border border-border">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-full bg-secondary flex items-center justify-center">
                    <span className="text-sm font-medium">{user.username?.[0]?.toUpperCase()}</span>
                  </div>
                  <div>
                    <p className="font-medium">{user.username}</p>
                    <p className="text-xs text-muted-foreground">{user.email}</p>
                  </div>
                </div>
                <div className="flex items-center gap-2">
                  <Badge className="capitalize">{user.role}</Badge>
                  <Badge className={user.kyc_status === 'verified' ? 'bg-green-600/20 text-green-400' : 'bg-gold-600/20 text-gold-400'}>
                    {user.kyc_status}
                  </Badge>
                </div>
              </div>
            ))}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export const SellerDashboardContent = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');

  const renderContent = () => {
    switch (activeTab) {
      case 'dashboard':
        if (user?.role === 'admin') return <AdminDashboard />;
        if (user?.role === 'seller' || user?.role === 'agent') return <SellerDashboard />;
        return <BuyerDashboard />;
      case 'admin':
        return <AdminDashboard />;
      case 'properties':
        // Navigate to properties page for browsing
        navigate('/properties');
        return null;
      case 'interests':
        return <ShortlistSection />;
      case 'offers':
        return <OffersSection />;
      case 'calendar':
        return <CalendarSection />;
      case 'settings':
        return <SettingsSection />;
      default:
        return <BuyerDashboard />;
    }
  };

  return (
    <DashboardLayout activeTab={activeTab} setActiveTab={setActiveTab}>
      {renderContent()}
    </DashboardLayout>
  );
};

// Shortlist Section Component
const ShortlistSection = () => {
  const [interests, setInterests] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchInterests = async () => {
      try {
        const res = await api.get('/interests');
        setInterests(res.data || []);
      } catch (err) {
        console.error('Failed to fetch interests:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchInterests();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="space-y-6" data-testid="shortlist-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl mb-1">My Shortlist</h2>
          <p className="text-sm text-muted-foreground">Properties you've shown interest in</p>
        </div>
        <Badge variant="outline">{interests.length} properties</Badge>
      </div>

      {interests.length === 0 ? (
        <Card className="glass-card border-white/10">
          <CardContent className="p-12 text-center">
            <Heart className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg font-medium mb-2">No properties shortlisted yet</p>
            <p className="text-sm text-muted-foreground mb-4">
              Browse properties and click the heart icon to add them here
            </p>
            <Button onClick={() => navigate('/properties')} data-testid="browse-properties-btn">
              <Search className="w-4 h-4 mr-2" /> Browse Properties
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {interests.map((interest) => (
            <Card 
              key={interest.id} 
              className="glass-card border-white/10 cursor-pointer hover:border-primary/30 transition-colors"
              onClick={() => navigate(`/property/${interest.property_id}`)}
              data-testid={`shortlist-item-${interest.id}`}
            >
              <CardContent className="p-4 flex items-center gap-4">
                <div className="w-16 h-16 rounded bg-secondary/50 flex items-center justify-center flex-shrink-0">
                  <Building2 className="w-6 h-6 text-muted-foreground" />
                </div>
                <div className="flex-1 min-w-0">
                  <p className="font-medium truncate">{interest.property_title || 'Property'}</p>
                  <p className="text-sm text-muted-foreground">Stage: {interest.stage?.replace('_', ' ')}</p>
                </div>
                <Badge className="capitalize">{interest.stage || 'interested'}</Badge>
                <ChevronRight className="w-5 h-5 text-muted-foreground" />
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Offers Section Component
const OffersSection = () => {
  const [offers, setOffers] = useState([]);
  const [loading, setLoading] = useState(true);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchOffers = async () => {
      try {
        const res = await api.get('/offers');
        setOffers(res.data || []);
      } catch (err) {
        console.error('Failed to fetch offers:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchOffers();
  }, []);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-8 h-8 border-2 border-primary/30 border-t-primary rounded-full animate-spin" />
      </div>
    );
  }

  const formatPrice = (price) => {
    if (price >= 1000000) return `AED ${(price / 1000000).toFixed(1)}M`;
    return `AED ${price?.toLocaleString()}`;
  };

  return (
    <div className="space-y-6" data-testid="offers-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl mb-1">Offers</h2>
          <p className="text-sm text-muted-foreground">Track your submitted offers</p>
        </div>
        <Badge variant="outline">{offers.length} offers</Badge>
      </div>

      {offers.length === 0 ? (
        <Card className="glass-card border-white/10">
          <CardContent className="p-12 text-center">
            <AEDIcon className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
            <p className="text-lg font-medium mb-2">No offers made yet</p>
            <p className="text-sm text-muted-foreground mb-4">
              When you're ready to make a move, submit an offer on a property
            </p>
            <Button onClick={() => navigate('/properties')} data-testid="browse-to-offer-btn">
              <Search className="w-4 h-4 mr-2" /> Browse Properties
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {offers.map((offer) => (
            <Card 
              key={offer.id} 
              className="glass-card border-white/10"
              data-testid={`offer-item-${offer.id}`}
            >
              <CardContent className="p-4">
                <div className="flex items-center justify-between mb-3">
                  <div className="flex items-center gap-3">
                    <div className="w-12 h-12 rounded bg-primary/10 flex items-center justify-center">
                      <AEDIcon className="w-5 h-5 text-primary" />
                    </div>
                    <div>
                      <p className="font-medium">{offer.property_title || 'Property'}</p>
                      <p className="text-xs text-muted-foreground">
                        Submitted {new Date(offer.created_at).toLocaleDateString()}
                      </p>
                    </div>
                  </div>
                  <Badge className={
                    offer.status === 'accepted' ? 'bg-green-600/20 text-green-400' :
                    offer.status === 'rejected' ? 'bg-red-600/20 text-red-400' :
                    offer.status === 'countered' ? 'bg-blue-600/20 text-blue-400' :
                    'bg-gold-600/20 text-gold-400'
                  }>
                    {offer.status}
                  </Badge>
                </div>
                <div className="flex items-center justify-between text-sm">
                  <span className="text-muted-foreground">Your Offer</span>
                  <span className="font-display text-primary text-lg">{formatPrice(offer.offer_amount)}</span>
                </div>
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

// Calendar Section Component
const CalendarSection = () => {
  const navigate = useNavigate();
  
  return (
    <div className="space-y-6" data-testid="calendar-section">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="font-display text-2xl mb-1">Calendar</h2>
          <p className="text-sm text-muted-foreground">Scheduled viewings and events</p>
        </div>
      </div>

      <Card className="glass-card border-white/10">
        <CardContent className="p-12 text-center">
          <Calendar className="w-12 h-12 text-muted-foreground mx-auto mb-4" />
          <p className="text-lg font-medium mb-2">No viewings scheduled</p>
          <p className="text-sm text-muted-foreground mb-4">
            Once you unlock a property, you can request a viewing here
          </p>
          <Button onClick={() => navigate('/properties')} data-testid="schedule-viewing-btn">
            <Search className="w-4 h-4 mr-2" /> Find Properties
          </Button>
        </CardContent>
      </Card>
    </div>
  );
};

// Settings Section Component
const SettingsSection = () => {
  const { user } = useAuth();
  
  return (
    <div className="space-y-6" data-testid="settings-section">
      <div>
        <h2 className="font-display text-2xl mb-1">Settings</h2>
        <p className="text-sm text-muted-foreground">Manage your account preferences</p>
      </div>

      <Card className="glass-card border-white/10">
        <CardHeader>
          <CardTitle className="text-lg">Profile Information</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="flex items-center justify-between py-2 border-b border-white/5">
            <span className="text-sm text-muted-foreground">Username</span>
            <span className="text-sm font-medium">{user?.username}</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-white/5">
            <span className="text-sm text-muted-foreground">Email</span>
            <span className="text-sm font-medium">{user?.email}</span>
          </div>
          <div className="flex items-center justify-between py-2 border-b border-white/5">
            <span className="text-sm text-muted-foreground">Role</span>
            <Badge className="capitalize">{user?.role}</Badge>
          </div>
          <div className="flex items-center justify-between py-2">
            <span className="text-sm text-muted-foreground">KYC Status</span>
            <Badge className={user?.kyc_status === 'verified' ? 'bg-green-600/20 text-green-400' : 'bg-gold-600/20 text-gold-400'}>
              {user?.kyc_status || 'pending'}
            </Badge>
          </div>
        </CardContent>
      </Card>

      <Card className="glass-card border-white/10">
        <CardHeader>
          <CardTitle className="text-lg">Verification Status</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid grid-cols-2 gap-4">
            <div className="p-4 rounded border border-white/10 text-center">
              <Shield className={`w-6 h-6 mx-auto mb-2 ${user?.kyc_status === 'verified' ? 'text-green-400' : 'text-muted-foreground'}`} />
              <p className="text-xs text-muted-foreground">KYC</p>
              <p className="text-sm font-medium capitalize">{user?.kyc_status || 'Pending'}</p>
            </div>
            <div className="p-4 rounded border border-white/10 text-center">
              <AEDIcon className={`w-6 h-6 mx-auto mb-2 ${user?.proof_of_funds_status === 'verified' ? 'text-green-400' : 'text-muted-foreground'}`} />
              <p className="text-xs text-muted-foreground">Proof of Funds</p>
              <p className="text-sm font-medium capitalize">{user?.proof_of_funds_status || 'Pending'}</p>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default function DashboardPage() {
  const { user } = useAuth();
  
  // Redirect buyers to the new Buyer Dashboard
  if (user?.role === 'buyer') {
    return <Navigate to="/buyer/dashboard" replace />;
  }
  
  // Redirect sellers to the Seller Dashboard
  if (user?.role === 'seller') {
    return <Navigate to="/seller/dashboard" replace />;
  }
  
  // Redirect admins to Admin Dashboard
  if (user?.role === 'admin') {
    return <Navigate to="/admin" replace />;
  }
  
  // Default dashboard for other roles
  return <DashboardContent />;
}

function DashboardContent() {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('dashboard');
  
  return (
    <DashboardLayout activeTab={activeTab} setActiveTab={setActiveTab}>
      <BuyerDashboard />
    </DashboardLayout>
  );
}
